import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  FileText, Mail, RefreshCw, ChevronDown, ChevronUp,
  Zap, Calendar, CalendarRange, Send, AlertTriangle,
  Clock, PlayCircle, ArrowUpRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPE_META = {
  daily_digest: { label: 'Daily Digest', icon: Calendar, color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  weekly_digest: { label: 'Weekly Digest', icon: CalendarRange, color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  immediate_alert: { label: 'Alert', icon: Zap, color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
};

const STATUS_META = {
  generated: { label: 'Generated', color: 'bg-secondary text-muted-foreground border-border' },
  sent: { label: 'Sent', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  mock_sent: { label: 'Mock Sent', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  failed: { label: 'Failed', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffHrs = (now - d) / (1000 * 60 * 60);
  if (diffHrs < 1) return `${Math.round((now - d) / 60000)}m ago`;
  if (diffHrs < 24) return `${Math.round(diffHrs)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function ReportsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [followupLogs, setFollowupLogs] = useState([]);
  const [followupRules, setFollowupRules] = useState([]);
  const [stats, setStats] = useState({});
  const [emails, setEmails] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  useEffect(() => { fetchAll(); }, [token]);

  const fetchAll = async () => {
    try {
      const [rRes, sRes, lRes, ruRes, eRes] = await Promise.all([
        axios.get(`${API}/reports`, { headers }),
        axios.get(`${API}/reports/stats`, { headers }),
        axios.get(`${API}/reports/followups/logs`, { headers }),
        axios.get(`${API}/reports/followups/rules`, { headers }),
        axios.get(`${API}/reports/emails/sent`, { headers }),
      ]);
      setReports(rRes.data || []);
      setStats(sRes.data || {});
      setFollowupLogs(lRes.data || []);
      setFollowupRules(ruRes.data || []);
      setEmails(eRes.data || []);
    } catch (err) {
      console.error('Reports fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generate = async (type) => {
    setGenerating(type);
    try {
      await axios.post(`${API}/reports/generate/${type}`, {}, { headers });
      toast.success(`${type === 'daily' ? 'Daily' : 'Weekly'} digest generated`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(null);
    }
  };

  const testFollowup = async () => {
    setGenerating('test');
    try {
      const { data } = await axios.post(`${API}/reports/followups/test`, {}, { headers });
      toast.success(`Test follow-up triggered (${data.triggered} actions)`);
      await fetchAll();
    } catch (err) {
      toast.error('Test follow-up failed');
    } finally {
      setGenerating(null);
    }
  };

  const toggleRule = async (ruleId) => {
    try {
      const { data } = await axios.put(`${API}/reports/followups/rules/${ruleId}/toggle`, {}, { headers });
      setFollowupRules(prev => prev.map(r => r.id === ruleId ? { ...r, is_active: data.is_active } : r));
      toast.success(`Rule ${data.is_active ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to toggle rule');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5 sm:space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Reports & Follow-ups</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Automated digests, alerts, and follow-up actions</p>
        </div>
        <Button
          size="sm" variant="ghost"
          onClick={() => navigate('/settings/reports')}
          className="text-xs text-muted-foreground hover:text-foreground gap-1"
          data-testid="goto-report-settings"
        >
          Settings <ArrowUpRight size={12} />
        </Button>
      </div>

      {/* Stat Chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip icon={<FileText size={16} />} value={stats.total ?? 0} label="Total Reports" accent="white" />
        <StatChip icon={<Calendar size={16} />} value={stats.daily_digests ?? 0} label="Daily Digests" accent="emerald" />
        <StatChip icon={<CalendarRange size={16} />} value={stats.weekly_digests ?? 0} label="Weekly Digests" accent="blue" />
        <StatChip icon={<Zap size={16} />} value={stats.immediate_alerts ?? 0} label="Alerts" accent="amber" />
      </div>

      {/* Generate Actions */}
      <Card className="border-border bg-card">
        <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            size="sm"
            onClick={() => generate('daily')}
            disabled={!!generating}
            className="bg-emerald-600 hover:bg-emerald-700 text-foreground gap-2"
            data-testid="generate-daily-btn"
          >
            {generating === 'daily' ? <RefreshCw size={14} className="animate-spin" /> : <Calendar size={14} />}
            Generate Daily Digest
          </Button>
          <Button
            size="sm"
            onClick={() => generate('weekly')}
            disabled={!!generating}
            className="bg-blue-600 hover:bg-blue-700 text-foreground gap-2"
            data-testid="generate-weekly-btn"
          >
            {generating === 'weekly' ? <RefreshCw size={14} className="animate-spin" /> : <CalendarRange size={14} />}
            Generate Weekly Digest
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={testFollowup}
            disabled={!!generating}
            className="border-border text-foreground hover:text-foreground gap-2"
            data-testid="test-followup-btn"
          >
            {generating === 'test' ? <RefreshCw size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            Test Follow-up
          </Button>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border pb-0 overflow-x-auto">
        {[
          { key: 'reports', label: 'Reports', count: reports.length },
          { key: 'followups', label: 'Follow-up Logs', count: followupLogs.length },
          { key: 'rules', label: 'Rules', count: followupRules.length },
          { key: 'emails', label: 'Emails', count: emails.length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-emerald-400 text-emerald-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label} <span className="ml-1 text-xs opacity-60">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'reports' && <ReportsTab reports={reports} expandedReport={expandedReport} setExpandedReport={setExpandedReport} />}
      {tab === 'followups' && <FollowupLogsTab logs={followupLogs} />}
      {tab === 'rules' && <RulesTab rules={followupRules} onToggle={toggleRule} />}
      {tab === 'emails' && <EmailsTab emails={emails} />}
    </div>
  );
}

function StatChip({ icon, value, label, accent }) {
  const colors = {
    white: 'text-foreground',
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
  };
  return (
    <Card className="border-border bg-card">
      <CardContent className="flex items-center gap-3 p-4">
        <span className="text-muted-foreground">{icon}</span>
        <div>
          <p className={`text-xl font-bold ${colors[accent]}`}>{value}</p>
          <p className="text-[10px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ReportsTab({ reports, expandedReport, setExpandedReport }) {
  if (reports.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No reports generated yet</p>
        <p className="text-xs text-muted-foreground mt-1">Click "Generate Daily Digest" to create your first report</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="reports-list">
      {reports.map(r => {
        const meta = REPORT_TYPE_META[r.report_type] || REPORT_TYPE_META.daily_digest;
        const statusMeta = STATUS_META[r.status] || STATUS_META.generated;
        const Icon = meta.icon;
        const isExpanded = expandedReport === r.id;
        return (
          <Card key={r.id} className="border-border bg-card overflow-hidden">
            <button
              onClick={() => setExpandedReport(isExpanded ? null : r.id)}
              className="w-full text-left p-4 flex items-center gap-3 hover:bg-muted transition-colors"
              data-testid={`report-${r.id}`}
            >
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${meta.color.split(' ')[0]}`}>
                <Icon size={16} className={meta.color.split(' ')[1]} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{r.subject}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{r.summary?.slice(0, 100)}</p>
              </div>
              <Badge className={`text-[10px] shrink-0 ${statusMeta.color}`}>{statusMeta.label}</Badge>
              <span className="text-xs text-muted-foreground shrink-0 w-20 text-right">{formatTime(r.created_at)}</span>
              {isExpanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
            </button>
            {isExpanded && (
              <div className="border-t border-border p-4">
                <div className="bg-background rounded-lg p-4 max-h-[400px] overflow-auto" data-testid="report-preview">
                  <div dangerouslySetInnerHTML={{ __html: r.html_content }} />
                </div>
                {r.insights?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Insights included:</p>
                    {r.insights.slice(0, 4).map((t, i) => (
                      <p key={i} className="text-xs text-muted-foreground pl-2 border-l border-border">{t}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function FollowupLogsTab({ logs }) {
  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <Send className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No follow-up actions yet</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="followup-logs-list">
      {logs.map(log => {
        const statusMeta = STATUS_META[log.status] || STATUS_META.generated;
        return (
          <Card key={log.id} className="border-border bg-card">
            <CardContent className="p-4 flex items-center gap-3">
              <Send size={14} className="text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{log.subject || log.trigger?.replace('_', ' ')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{log.action} &middot; {log.recipient || 'N/A'}</p>
              </div>
              <Badge className={`text-[10px] shrink-0 ${statusMeta.color}`}>{statusMeta.label}</Badge>
              <span className="text-xs text-muted-foreground shrink-0">{formatTime(log.created_at)}</span>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function RulesTab({ rules, onToggle }) {
  return (
    <div className="space-y-2" data-testid="followup-rules-list">
      {rules.map(rule => (
        <Card key={rule.id} className="border-border bg-card">
          <CardContent className="p-4 flex items-center gap-3">
            <Zap size={14} className={rule.is_active ? 'text-amber-400' : 'text-muted-foreground'} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground">{rule.rule_name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Trigger: <span className="text-muted-foreground">{rule.trigger}</span> &middot; Action: <span className="text-muted-foreground">{rule.action}</span>
              </p>
            </div>
            <Button
              size="sm" variant="ghost"
              onClick={() => onToggle(rule.id)}
              className={`text-xs h-7 ${rule.is_active ? 'text-emerald-400 hover:text-primary' : 'text-muted-foreground hover:text-muted-foreground'}`}
              data-testid={`toggle-rule-${rule.id}`}
            >
              {rule.is_active ? 'Active' : 'Disabled'}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EmailsTab({ emails }) {
  const [expanded, setExpanded] = useState(null);
  if (emails.length === 0) {
    return (
      <div className="text-center py-12">
        <Mail className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No emails sent yet</p>
        <p className="text-xs text-muted-foreground mt-1">Emails will appear here when reports or follow-ups are triggered</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="emails-list">
      {emails.map(e => (
        <Card key={e.id} className="border-border bg-card overflow-hidden">
          <button
            onClick={() => setExpanded(expanded === e.id ? null : e.id)}
            className="w-full text-left p-4 flex items-center gap-3 hover:bg-muted transition-colors"
            data-testid={`email-${e.id}`}
          >
            <Mail size={14} className="text-blue-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground truncate">{e.subject}</p>
              <p className="text-xs text-muted-foreground mt-0.5">To: {e.to} &middot; From: {e.from_name}</p>
            </div>
            <Badge className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20 shrink-0">{e.status}</Badge>
            <span className="text-xs text-muted-foreground shrink-0">{formatTime(e.created_at)}</span>
          </button>
          {expanded === e.id && (
            <div className="border-t border-border p-4">
              <div className="bg-background rounded-lg p-4 max-h-[300px] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: e.html_content }} />
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
